import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  MapPin, Calendar, Clock, DollarSign, CheckCircle, 
  FileText, Image, Phone, CreditCard, Mail, Receipt, Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerPortal() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchJobData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token]);

  const fetchJobData = async () => {
    try {
      const response = await axios.get(`${API}/customer/${token}`);
      setData(response.data);
    } catch (err) {
      setError('Job not found or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    
    try {
      const response = await axios.get(`${API}/customer/${token}/payment/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Thank you.');
        fetchJobData(); // Refresh to show paid status
        setCheckingPayment(false);
        // Clear URL params
        window.history.replaceState({}, '', `/customer/${token}`);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      setCheckingPayment(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    try {
      const [hour, min] = timeStr.split(':').map(Number);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const handlePay = async () => {
    if (!data?.business) return;
    
    // For Venmo/CashApp, just open the link
    if (data.business.payment_method === 'venmo' || data.business.payment_method === 'cashapp') {
      if (data.business.payment_link) {
        window.open(data.business.payment_link, '_blank');
      }
      return;
    }
    
    // For Stripe, create checkout session
    if (data.business.payment_method === 'stripe' || data.business.payment_link === 'stripe') {
      setPaymentLoading(true);
      try {
        const response = await axios.post(`${API}/customer/${token}/pay`, {
          origin_url: window.location.origin
        });
        
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      } catch (error) {
        const message = error.response?.data?.detail || 'Failed to start payment';
        toast.error(message);
      } finally {
        setPaymentLoading(false);
      }
    }
  };

  const handleOpenMaps = () => {
    if (data?.job?.address) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        window.location.href = `maps://maps.apple.com/?q=${encodeURIComponent(data.job.address)}`;
      } else {
        window.open(`https://maps.google.com/?q=${encodeURIComponent(data.job.address)}`, '_blank');
      }
    }
  };

  const statusInfo = {
    quote: { label: 'Quote Sent', color: 'bg-blue-500', icon: FileText },
    scheduled: { label: 'Scheduled', color: 'bg-amber-500', icon: Calendar },
    paid: { label: 'Paid', color: 'bg-emerald-500', icon: CheckCircle }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-pulse text-slate-600 font-heading text-xl">Loading...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-slate-400" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-700 mb-2">Job Not Found</h1>
          <p className="text-slate-500">This link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  const { job, business } = data;
  const status = statusInfo[job.status] || statusInfo.quote;
  const StatusIcon = status.icon;

  // Calculate subtotal
  const laborAmount = job.labor_amount || 0;
  const materialsAmount = job.materials_amount || 0;
  const subtotal = laborAmount + materialsAmount;
  const taxAmount = job.tax_amount || 0;
  const taxBreakdown = job.tax_breakdown || [];
  const total = job.quote_amount || subtotal + taxAmount;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="customer-portal">
      {/* Header with Logo */}
      <div className="bg-slate-dark text-white p-6">
        <div className="max-w-lg mx-auto">
          {/* Business Logo */}
          {business.logo && (
            <div className="mb-4">
              <img 
                src={business.logo} 
                alt={`${business.name} logo`}
                className="h-16 max-w-[200px] object-contain bg-white rounded-lg p-2"
              />
            </div>
          )}
          
          <p className="text-slate-400 text-sm mb-1">Job Details from</p>
          <h1 className="font-heading text-2xl font-bold">{business.name}</h1>
          {(business.phone || business.email) && (
            <div className="flex flex-wrap gap-4 mt-2 text-slate-300 text-sm">
              {business.phone && (
                <a href={`tel:${business.phone}`} className="flex items-center gap-1 hover:text-white">
                  <Phone className="w-4 h-4" />
                  {business.phone}
                </a>
              )}
              {business.email && (
                <a href={`mailto:${business.email}`} className="flex items-center gap-1 hover:text-white">
                  <Mail className="w-4 h-4" />
                  {business.email}
                </a>
              )}
            </div>
          )}
          {business.address && (
            <p className="text-slate-400 text-sm mt-1">{business.address}</p>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 -mt-4">
        {/* Status Card */}
        <div className="bg-white rounded-xl border-2 border-slate-200 shadow-lg overflow-hidden mb-4">
          <div className={`${status.color} p-4 flex items-center gap-3`}>
            <StatusIcon className="w-6 h-6 text-white" />
            <div>
              <p className="text-white/80 text-sm">Status</p>
              <p className="text-white font-bold text-lg">{status.label}</p>
            </div>
          </div>

          <div className="p-4">
            <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">
              Hi {job.client_name}!
            </h2>
            <p className="text-slate-600 mb-4">{job.description}</p>

            {/* Schedule Info */}
            {job.scheduled_date && (
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 text-[#FF5F00]" />
                  <span className="font-medium text-slate-900">
                    {formatDate(job.scheduled_date)}
                  </span>
                </div>
                {job.scheduled_time && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#FF5F00]" />
                    <span className="font-medium text-slate-900">
                      {formatTime(job.scheduled_time)}
                      {job.duration && ` (${job.duration} min)`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Address */}
            {job.address && (
              <button
                onClick={handleOpenMaps}
                className="w-full bg-slate-50 rounded-lg p-4 mb-4 flex items-center gap-3 hover:bg-slate-100 transition-colors text-left"
              >
                <div className="w-10 h-10 bg-[#FF5F00] rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-slate-900 font-medium">{job.address}</p>
                  <p className="text-[#FF5F00] text-sm font-bold">TAP TO OPEN IN MAPS</p>
                </div>
              </button>
            )}

            {/* Service Notes - Customer visible */}
            {job.customer_notes && (
              <div className="bg-emerald-50 rounded-lg p-4 mb-4 border border-emerald-200">
                <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-emerald-600" />
                  Service Notes
                </h3>
                <p className="text-slate-700 whitespace-pre-wrap text-sm">
                  {job.customer_notes}
                </p>
              </div>
            )}

            {/* Price Breakdown */}
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Price Breakdown
              </h3>
              
              <div className="space-y-2 text-sm">
                {laborAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Labor</span>
                    <span className="text-slate-900 font-medium">${laborAmount.toFixed(2)}</span>
                  </div>
                )}
                
                {materialsAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Materials</span>
                    <span className="text-slate-900 font-medium">${materialsAmount.toFixed(2)}</span>
                  </div>
                )}
                
                {(laborAmount > 0 || materialsAmount > 0) && (
                  <div className="flex justify-between border-t border-slate-200 pt-2">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-900 font-medium">${subtotal.toFixed(2)}</span>
                  </div>
                )}
                
                {/* Tax Breakdown */}
                {taxBreakdown.length > 0 && taxBreakdown.map((tax, index) => (
                  <div key={index} className="flex justify-between text-slate-600">
                    <span>{tax.name} ({tax.rate}%)</span>
                    <span className="text-slate-900 font-medium">${tax.amount.toFixed(2)}</span>
                  </div>
                ))}
                
                {taxAmount > 0 && taxBreakdown.length === 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Tax</span>
                    <span className="text-slate-900 font-medium">${taxAmount.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Total Amount */}
            <div className="flex items-center justify-between py-4 border-t-2 border-slate-200">
              <span className="text-slate-700 font-bold text-lg">Total</span>
              <span className="font-heading text-3xl font-bold text-[#FF5F00]">
                ${total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Photos */}
        {job.photos && job.photos.length > 0 && (
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-lg p-4 mb-4">
            <h3 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Image className="w-5 h-5" />
              Job Photos
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {job.photos.map((photo, index) => (
                <div key={photo.id || index} className="relative">
                  <img
                    src={photo.data}
                    alt={photo.description || `Photo ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  {photo.description && (
                    <p className="text-xs text-slate-500 mt-1">{photo.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Button */}
        {(business.payment_link || business.payment_method === 'stripe') && job.status !== 'paid' && (
          <>
            {checkingPayment ? (
              <div className="w-full h-14 bg-amber-100 text-amber-700 font-bold text-lg rounded-xl flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Verifying Payment...
              </div>
            ) : (
              <Button
                onClick={handlePay}
                disabled={paymentLoading}
                data-testid="pay-now-btn"
                className="w-full h-14 bg-[#FF5F00] hover:bg-orange-600 text-white font-bold text-lg rounded-xl shadow-lg disabled:opacity-50"
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay ${total.toFixed(2)} via {
                      business.payment_method === 'venmo' ? 'Venmo' : 
                      business.payment_method === 'cashapp' ? 'Cash App' : 
                      'Card'
                    }
                  </>
                )}
              </Button>
            )}
          </>
        )}

        {job.status === 'paid' && (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 text-center">
            <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-emerald-700 font-bold">Payment Received - Thank You!</p>
          </div>
        )}

        {/* Footer with Logo */}
        <div className="text-center mt-6">
          {business.logo && (
            <img 
              src={business.logo} 
              alt={business.name}
              className="h-10 mx-auto mb-2 opacity-50"
            />
          )}
          <p className="text-slate-400 text-sm">
            Powered by MyJobTools
          </p>
        </div>
      </div>
    </div>
  );
}
